/**
 * Card brand art, decline-code messaging, and phone-collection copy.
 *
 * Split out of utils/Constants.js (roadmap Module 8) — Constants.js remains
 * a re-export shim until import sites migrate to "src/constants/payments".
 */
export const CARD_BRAND_SVGS = {
  visa: (
    <svg
      width="36"
      height="23"
      viewBox="0 0 38 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{ flexShrink: 0, borderRadius: "3px" }}
    >
      <rect width="38" height="24" rx="3" fill="#1A1F71" />
      <path
        d="M15.5 16.5H13l1.562-9.5h2.5L15.5 16.5zm8.5-9.3c-.5-.19-1.28-.4-2.25-.4-2.48 0-4.23 1.32-4.24 3.2-.02 1.4 1.25 2.17 2.2 2.64.97.47 1.3.78 1.3 1.2-.01.64-.78.93-1.5.93-.99 0-1.52-.15-2.34-.52l-.32-.15-.35 2.15c.58.27 1.65.5 2.76.51 2.6 0 4.3-1.29 4.32-3.28.01-1.09-.65-1.92-2.07-2.6-.86-.44-1.39-.74-1.38-1.19 0-.4.44-.82 1.4-.82.8-.02 1.38.17 1.83.37l.22.1.34-2.14zm6.26-.2h-1.94c-.6 0-1.05.17-1.31.79l-3.72 8.91h2.63s.43-1.19.53-1.45h3.21c.07.34.3 1.45.3 1.45h2.32L30.26 7zm-3.09 6.18c.21-.56 1-2.71 1-2.71s.21-.56.33-.92l.17.83s.48 2.29.58 2.8h-2.08zM12 7l-2.46 6.47-.26-1.34C8.83 10.54 7.43 9 5.88 8.23L8.13 16.5h2.65L14.66 7H12z"
        fill="#fff"
      />
      <path d="M7.12 7H3.02L3 7.2c3.18.81 5.29 2.77 6.16 5.13L8.26 7.8C8.09 7.2 7.66 7.02 7.12 7z" fill="#F9A533" />
    </svg>
  ),
  mastercard: (
    <svg
      width="36"
      height="23"
      viewBox="0 0 38 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{ flexShrink: 0, borderRadius: "3px" }}
    >
      <rect width="38" height="24" rx="3" fill="#252525" />
      <circle cx="15" cy="12" r="7" fill="#EB001B" />
      <circle cx="23" cy="12" r="7" fill="#F79E1B" />
      <path d="M19 7.01a7 7 0 0 1 0 9.98A7 7 0 0 1 19 7.01z" fill="#FF5F00" />
    </svg>
  ),
  american_express: (
    <svg
      width="36"
      height="23"
      viewBox="0 0 38 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{ flexShrink: 0, borderRadius: "3px" }}
    >
      <rect width="38" height="24" rx="3" fill="#2557D6" />
      <text x="6" y="17" fontFamily="Arial, sans-serif" fontSize="10" fontWeight="bold" fill="#fff">
        AMEX
      </text>
    </svg>
  ),
  discover: (
    <svg
      width="36"
      height="23"
      viewBox="0 0 38 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{ flexShrink: 0, borderRadius: "3px" }}
    >
      <rect width="38" height="24" rx="3" fill="#fff" stroke="#E5E7EB" strokeWidth="1" />
      <circle cx="25" cy="12" r="7" fill="#F76F20" />
      <text x="5" y="16" fontFamily="Arial, sans-serif" fontSize="7" fontWeight="bold" fill="#231F20">
        DISCOVER
      </text>
    </svg>
  ),
  default: (
    <svg
      width="36"
      height="23"
      viewBox="0 0 38 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{ flexShrink: 0, borderRadius: "3px" }}
    >
      <rect width="38" height="24" rx="3" fill="#E5E7EB" />
      <rect x="5" y="8" width="28" height="5" rx="1" fill="#9CA3AF" />
    </svg>
  ),
};

// Phone collection banner / modal
export const PHONE_COLLECTION_STORAGE_KEY = "phone_banner_dismissed_count";
export const PHONE_COLLECTION_MAX_DISMISSALS = 3;
export const PHONE_COLLECTION_CONSENT_TEXT =
  "I consent to receive SMS messages from Inhouse AI, including updates and relevant updates related to my activity.";

export const PHONE_COLLECTION_COPY = [
  { headline: "Add your phone number for faster case updates", cta: "Add number", dismiss: "Not now" },
  { headline: "Don't miss critical updates on your matters", cta: "Add number", dismiss: "Remind me later" },
  { headline: "Last reminder — add your phone for real-time updates", cta: "Add number", dismiss: "No thanks" },
];

export const DECLINE_CODE_MESSAGES = {
  cardNumberInvalid: {
    message: "Your card number is invalid.",
    hint: "Please double-check your card number and try again.",
  },
  incorrect_number: {
    message: "Your card number is incorrect.",
    hint: "Please double-check your card number and try again.",
  },
  invalid_cvc: {
    message: "Your card's security code (CVV) is invalid.",
    hint: "Please check the 3-digit CVV on the back of your card and try again.",
  },
  incorrect_cvc: {
    message: "Your card's security code (CVV) is incorrect.",
    hint: "Please check the 3-digit CVV on the back of your card and try again.",
  },
  cardCvvInvalid: {
    message: "Your card's security code (CVV) is invalid.",
    hint: "Please check the 3-digit CVV on the back of your card and try again.",
  },
  card_declined: {
    message: "Your card was declined.",
    hint: "Please try a different card or contact your bank for more information.",
  },
  ChargebeeClientError: {
    message: "There was an issue processing your payment.",
    hint: "Please refresh the page and try again. If the issue persists, contact support at support@inhouse.ai.",
  },
  empty_response: {
    message: "We didn't receive a response from your bank.",
    hint: "Please try again in a moment. If the issue persists, contact support at support@inhouse.ai.",
  },
};
